import { useCallback, useEffect, useMemo, useState } from 'react';

import { getValues } from '../utils/record';

type Options = {
  enableReinitialize?: boolean;
};

type SetInput<T extends SetSupportedValue = SetSupportedValue> = Iterable<T>;

export type SetReturn<T extends SetSupportedValue = SetSupportedValue> = ReturnType<
  typeof useSet<T>
>;

export type SetSupportedValue = string | number | bigint | boolean | undefined | symbol | null;

const DEFAULT_OPTIONS = {
  enableReinitialize: true,
} satisfies Options;

const useSet = <T extends SetSupportedValue>(
  input?: SetInput<T> | null | undefined,
  options?: Options,
) => {
  const [set, setSet] = useState(() => new Set(input));

  const enableReinitialize = options?.enableReinitialize ?? DEFAULT_OPTIONS.enableReinitialize;

  const append = useCallback(
    (value: T) =>
      setSet((prevSet) => {
        if (prevSet.has(value)) return prevSet;

        return new Set(prevSet).add(value);
      }),
    [],
  );

  const difference = useCallback(
    <TOther extends SetSupportedValue>(other: SetInput<TOther>) => {
      const otherSet = new Set(other);

      return set.difference(otherSet);
    },
    [set],
  );

  const forEach = useCallback((callback: (value: T) => void) => set.forEach(callback), [set]);

  const has = useCallback((value: T) => set.has(value), [set]);

  const intersection = useCallback(
    <TOther extends SetSupportedValue>(other: SetInput<TOther>) => {
      const otherSet = new Set(other);

      return set.intersection(otherSet);
    },
    [set],
  );

  const isDisjointFrom = useCallback(
    (other: SetInput) => {
      const otherSet = new Set(other);

      return set.isDisjointFrom(otherSet);
    },
    [set],
  );

  const isSubsetOf = useCallback(
    (other: SetInput) => {
      const otherSet = new Set(other);

      return set.isSubsetOf(otherSet);
    },
    [set],
  );

  const isSupersetOf = useCallback(
    (other: SetInput) => {
      const otherSet = new Set(other);

      return set.isSupersetOf(otherSet);
    },
    [set],
  );

  const remove = useCallback(
    (value: T) =>
      setSet((prevSet) => {
        if (!prevSet.has(value)) return prevSet;

        const nextSet = new Set(prevSet);

        nextSet.delete(value);

        return nextSet;
      }),
    [],
  );

  const removeAll = useCallback(
    () => setSet((prevSet) => (prevSet.size === 0 ? prevSet : new Set())),
    [],
  );

  const symmetricDifference = useCallback(
    <TOther extends SetSupportedValue>(other: SetInput<TOther>) => {
      const otherSet = new Set(other);

      return set.symmetricDifference(otherSet);
    },
    [set],
  );

  const union = useCallback(
    <TOther extends SetSupportedValue>(other: SetInput<TOther>) => {
      const otherSet = new Set(other);

      return set.union(otherSet);
    },
    [set],
  );

  // no point in returning .keys() since they're identical
  const values = useCallback(() => getValues(set), [set]);

  useEffect(() => {
    if (!enableReinitialize) return;

    setSet((prevSet) => {
      const nextSet = new Set(input);

      if (nextSet.size !== prevSet.size) return nextSet;

      return getValues(nextSet).every((value) => prevSet.has(value)) ? prevSet : nextSet;
    });
  }, [enableReinitialize, input]);

  return useMemo(
    () => ({
      append,
      difference,
      forEach,
      has,
      intersection,
      isDisjointFrom,
      isEmpty: set.size === 0,
      isSubsetOf,
      isSupersetOf,
      remove,
      removeAll,
      set,
      size: set.size,
      symmetricDifference,
      union,
      values,
    }),
    [
      append,
      difference,
      forEach,
      has,
      intersection,
      isDisjointFrom,
      isSubsetOf,
      isSupersetOf,
      remove,
      removeAll,
      symmetricDifference,
      union,
      values,
      set,
    ],
  );
};

export default useSet;
